import { useFeatureFlags } from "@evidenceb/athena-common/modules/FeatureFlags";
import type { Objective } from "@evidenceb/gameplay-interfaces";
import { useContext, useEffect, useState } from "react";
import { configStore } from "../../contexts/ConfigContext";
import { sessionStore } from "../../contexts/SessionContext";
import type { Config } from "../../interfaces/Config";
import { UserType } from "../../interfaces/User";
import * as localStorage from "../../utils/localStorage";
import { getCurrentAgent } from "../../utils/statements/statement-builder";
import { CacheNames } from "../../utils/sw-cache";
import useAthenaAPIClient from "../queries/useAthenaAPIClient/useAthenaAPIClient";
import { useUserId } from "../useUserInfo";
import { type GlobalConfig, getLearningEntries } from "./useInitApp";

export default function useInitServiceWorker() {
  const athenaAPIClient = useAthenaAPIClient();
  const [isWorkerReady, setIsWorkerReady] = useState<boolean>(false);
  const featureFlags = useFeatureFlags();
  const userId = useUserId();
  const {
    session: { evidencebId, fragments },
  } = useContext(sessionStore);
  const {
    config: { declinaison },
  } = useContext(configStore);

  useEffect(() => {
    if (!featureFlags.offlineFeatures) return;

    if ("serviceWorker" in navigator && !isWorkerReady) {
      navigator.serviceWorker.ready.then((registration) => {
        console.log("🚀 ~ ATHENA SW READY registration:", registration);

        setIsWorkerReady(true);
      });
    }
  }, [isWorkerReady, featureFlags]);

  // these webservices are called before the service worker is initialized and activated
  // so we need a 2nd round of API calls that allows the service worker to put them in cache
  useEffect(() => {
    if (!featureFlags.offlineFeatures) return;

    async function isCachePopulated(): Promise<boolean> {
      const hasConfig = await caches.has(CacheNames.config);
      const hasUserInfo = await caches.has(CacheNames.userInfo);
      const hasStudent = await caches.has(CacheNames.userStudent);
      const hasTeacher = await caches.has(CacheNames.userTeacher);
      const hasQuestions = await caches.has(CacheNames.data);
      const hasStatements = await caches.has(CacheNames.statements);
      const hasMappingTree = await caches.has(CacheNames.mappingTree);
      const hasLearningSets = await caches.has(CacheNames.learningSets);
      return Promise.resolve(
        hasConfig &&
          hasQuestions &&
          hasStatements &&
          hasUserInfo &&
          (hasTeacher || hasStudent) &&
          hasMappingTree &&
          hasLearningSets,
      );
    }

    async function triggerCachePopulation(): Promise<void> {
      let globalConfig: GlobalConfig;
      let tempGlobalconfig: Omit<GlobalConfig, "config"> & {
        config: Config;
      };
      const version = localStorage.getItem<string>(localStorage.Key.VERSION);
      let userType: UserType;
      athenaAPIClient.getTokenPayload().then((tokenPayload) => {
        if (
          tokenPayload &&
          tokenPayload.role === UserType.Student.toLowerCase()
        ) {
          userType = UserType.Student;
          athenaAPIClient.getStudent(userId);
        } else if (
          tokenPayload &&
          tokenPayload.role === UserType.Teacher.toLowerCase()
        ) {
          userType = UserType.Teacher;
          athenaAPIClient.getTeacher(userId);
        }
        athenaAPIClient.getResourcesLockStatus(userId, userType);
      });
      if (version) {
        // Same URL as the init request so the service worker caches the
        // config the app actually uses.
        globalConfig = await athenaAPIClient.getGlobalConfig(
          version,
          fragments,
        );
        athenaAPIClient.getData(
          version,
          featureFlags["init-bandit-manchot-with-adaptive-test"],
        );
        // TEMP
        tempGlobalconfig = {
          ...globalConfig,
          config: {
            ...globalConfig.config,
            ai: globalConfig.config.ai,
          },
        };
        if (featureFlags.requireMapping) {
          athenaAPIClient.getMappingTree();
          const learningEntries = getLearningEntries(tempGlobalconfig);

          if (learningEntries.length !== 0) {
            const learningSet: { [key: string]: Objective } = {};
            await Promise.all(
              learningEntries.map(async (learningSetId) => {
                const newLearningSet = await athenaAPIClient.getLearningSet(
                  learningSetId!,
                  // Depth of objectives, in which lay mapping information
                  { depth: "3" },
                );
                Object.assign(learningSet, newLearningSet);
              }),
            );
          }
        }
      }

      const agent = getCurrentAgent(declinaison, evidencebId);
      athenaAPIClient.getStatements(agent);
      // TODO: should we also cache fetchEvents?
    }

    async function initAfterServiceWorkerActivated() {
      const cacheIsPopulated = await isCachePopulated();
      if (!cacheIsPopulated) {
        if (isWorkerReady && userId) {
          await triggerCachePopulation();
        }
      }
      return;
    }

    initAfterServiceWorkerActivated();
  }, [
    athenaAPIClient,
    declinaison,
    evidencebId,
    featureFlags,
    fragments,
    isWorkerReady,
    userId,
  ]);
}
